<template>
  <portal-layout>
    <div v-loading="loading" class="detail-page">
      <div v-if="!loading && !product.id" class="empty-tip">商品不存在或已下架</div>

      <template v-if="product.id">
        <!-- 商品主信息 -->
        <div class="product-main">
          <div class="product-cover">
            <img :src="product.mainPic" :alt="product.title" />
          </div>
          <div class="product-side">
            <div class="category-tag">
              <el-tag size="small" effect="plain">{{ product.categoryName || '课程' }}</el-tag>
            </div>
            <h2 class="product-title">{{ product.title }}</h2>
            <p class="product-subtitle">{{ product.subtitle }}</p>

            <div class="format-row">
              <span class="label">上课形式：</span>
              <span class="value" :class="product.teachingFormat === 'live' ? 'live' : 'recorded'">
                {{ product.teachingFormat === 'live' ? '直播' : '录播' }}
              </span>
            </div>

            <div class="price-box">
              <span class="current-price">¥{{ product.actualPrice }}</span>
              <span class="original-price">¥{{ product.totalPrice }}</span>
              <span v-if="showDiscount" class="discount-tag">{{ product.discount }}折</span>
            </div>

            <div class="info-list">
              <div class="info-item">
                <span class="label">课程名称：</span>
                <span class="value">{{ product.courseName }}</span>
              </div>
              <div class="info-item">
                <span class="label">课程次数：</span>
                <span class="value">{{ product.courseNum }} 次</span>
              </div>
              <div class="info-item">
                <span class="label">库　　存：</span>
                <span class="value" :class="{ 'low-stock': product.stockNum < 10 }">
                  {{ product.stockNum }} 件
                </span>
              </div>
            </div>

            <div class="action-bar">
              <el-button size="medium" class="action-btn"
                         :class="{ 'is-active': isFavorite }" @click="toggleFavorite">
                <i :class="isFavorite ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
                {{ isFavorite ? '已收藏' : '收藏' }}
              </el-button>
              <el-button size="medium" class="action-btn"
                         :class="{ 'is-active': isCar }" @click="toggleCar">
                <i class="el-icon-shopping-cart-full"></i>
                {{ isCar ? '已加购' : '加入购物车' }}
              </el-button>
              <el-button type="primary" size="medium" class="buy-btn"
                         :disabled="product.status !== '1'" @click="buyNow">
                {{ product.status === '1' ? '立即购买' : '已下架' }}
              </el-button>
              <el-button type="text" class="contact-btn" @click="goContact">咨询老师</el-button>
            </div>
            <p class="buy-tip">支付过程中遇到问题，可点击「咨询老师」联系课程老师</p>
          </div>
        </div>

        <!-- 课程介绍 -->
        <div class="desc-card">
          <div class="section-title">课程介绍</div>
          <!-- 后端返回富文本 HTML，此处用 v-html 渲染 -->
          <div class="rich-content" v-html="product.description"></div>
          <div v-if="!product.description" class="empty-tip small">暂无课程介绍</div>
        </div>
      </template>

      <!-- 微信扫码支付 -->
      <pay-qrcode-dialog ref="payDialog" @success="onPaySuccess" />
    </div>
  </portal-layout>
</template>
<script>
import PortalLayout from '@/components/PortalLayout.vue';
import PayQrcodeDialog from '@/components/PayQrcodeDialog.vue';
import api from '@/common/api';
import storage from '@/common/storage.js';

export default {
  name: 'PortalCommodityDetail',
  components: { PortalLayout, PayQrcodeDialog },
  data() {
    return {
      loading: false,
      commId: null,
      product: {},
      isFavorite: false,
      isCar: false
    }
  },
  computed: {
    // 折扣为 10（即原价）时不展示折扣标签
    showDiscount() {
      const d = parseFloat(this.product.discount);
      return !isNaN(d) && d < 10;
    }
  },
  created() {
    this.commId = this.$route.query.id;
    if (!this.commId) {
      this.$message.error('缺少商品参数');
      return;
    }
    this.queryComm();
    // 收藏/加购状态需要登录，未登录时不查询（避免 401 跳登录）
    if (storage.getString('portalMember')) {
      this.queryFavoriteStatus();
      this.queryCarStatus();
    }
  },
  methods: {
    queryComm() {
      this.loading = true;
      // 后端为 @RequestParam commId，走 query
      api.query_comm({ commId: this.commId }).then((r) => {
        if (r.data.code === "200" && r.data.data) {
          this.product = r.data.data;
          document.title = this.product.title ? this.product.title + ' - 小程童信奥' : document.title;
        }
      }).catch(() => {}).finally(() => {
        this.loading = false;
      });
    },
    queryFavoriteStatus() {
      api.is_shop_favorite({ commId: this.commId }).then((r) => {
        if (r.data.code === "200") {
          // 返回 "1"/"0"
          this.isFavorite = r.data.data === "1";
        }
      }).catch(() => {});
    },
    queryCarStatus() {
      api.is_shop_car({ commId: this.commId }).then((r) => {
        if (r.data.code === "200") {
          this.isCar = r.data.data === "1";
        }
      }).catch(() => {});
    },
    // 收藏/加购属于需要登录的操作，未登录先引导登录
    requireLogin() {
      if (storage.getString('portalMember')) {
        return true;
      }
      this.$router.push({
        name: 'PortalLogin',
        query: { redirect: this.$route.fullPath }
      }).catch(() => {});
      return false;
    },
    toggleFavorite() {
      if (!this.requireLogin()) return;
      api.update_shop_favorite({ commId: this.commId }).then((r) => {
        if (r.data.code === "200") {
          // 后端 shopFavorite 分支返回 R.ok(!isExists) → Boolean，JSON 序列化为 true/false；
          // 而同域 is_shop_favorite 返回的是 "1"/"0"，两个接口格式不统一，这里两种都兼容
          this.isFavorite = r.data.data === true || String(r.data.data) === "1";
          this.$message.success(this.isFavorite ? '收藏成功' : '已取消收藏');
        }
      }).catch(() => {});
    },
    toggleCar() {
      if (!this.requireLogin()) return;
      api.update_shop_car({ commId: this.commId }).then((r) => {
        if (r.data.code === "200") {
          // 同 toggleFavorite：update_shop_car 返回 Boolean，需与 "1" 一并兼容
          this.isCar = r.data.data === true || String(r.data.data) === "1";
          this.$message.success(this.isCar ? '加购成功' : '已取消加购');
        }
      }).catch(() => {});
    },
    // 立即购买：门户端走微信 Native 扫码支付
    buyNow() {
      if (this.product.status !== '1') {
        this.$message.warning('商品已下架，无法购买');
        return;
      }
      if (!this.requireLogin()) return;
      this.$refs.payDialog.open({
        totalPrice: this.product.actualPrice,
        items: [{
          commodityCode: this.product.commodityCode,
          quantity: 1,
          price: this.product.actualPrice
        }],
        amount: this.product.actualPrice
      });
    },
    // 支付成功后回到我的订单，后续核销等操作在订单详情页完成
    onPaySuccess() {
      this.$router.push('/portal/order/list').catch(() => {});
    },
    goContact() {
      this.$router.push('/portal/shop/contact').catch(() => {});
    }
  }
};
</script>
<style scoped>
.detail-page {
  min-height: 300px;
}
.product-main {
  display: flex;
  gap: 30px;
  background-color: #fff;
  border-radius: 8px;
  padding: 25px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.product-cover {
  flex-shrink: 0;
  width: 400px;
  height: 300px;
  border-radius: 6px;
  overflow: hidden;
  background-color: #f5f7fa;
}
.product-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.product-side {
  flex: 1;
  min-width: 0;
}
.category-tag {
  margin-bottom: 10px;
}
.product-title {
  font-size: 22px;
  color: #303133;
  margin: 0 0 10px 0;
  line-height: 1.4;
}
.product-subtitle {
  font-size: 14px;
  color: #909399;
  margin: 0 0 15px 0;
  line-height: 1.6;
}
.format-row {
  font-size: 14px;
  margin-bottom: 15px;
}
.format-row .label {
  color: #909399;
}
.format-row .value.live {
  color: #F56C6C;
}
.format-row .value.recorded {
  color: #1890FF;
}
.price-box {
  background-color: #fef8f8;
  border-radius: 6px;
  padding: 15px 20px;
  margin-bottom: 18px;
}
.current-price {
  font-size: 28px;
  color: #F56C6C;
  font-weight: bold;
  margin-right: 12px;
}
.original-price {
  font-size: 14px;
  color: #c0c4cc;
  text-decoration: line-through;
  margin-right: 12px;
}
.discount-tag {
  display: inline-block;
  padding: 1px 8px;
  font-size: 12px;
  color: #fff;
  background-color: #F56C6C;
  border-radius: 3px;
  vertical-align: middle;
}
.info-list {
  border-top: 1px solid #ebeef5;
  padding-top: 15px;
  margin-bottom: 20px;
}
.info-item {
  font-size: 14px;
  color: #606266;
  line-height: 26px;
}
.info-item .label {
  color: #909399;
}
.info-item .value.low-stock {
  color: #E6A23C;
}
.action-bar {
  display: flex;
  align-items: center;
  gap: 12px;
}
.action-btn {
  min-width: 120px;
}
.action-btn.is-active {
  color: #E6A23C;
  border-color: #E6A23C;
}
.buy-btn {
  min-width: 160px;
}
.contact-btn {
  font-size: 13px;
  color: #909399;
}
.contact-btn:hover {
  color: #2B7EF5;
}
.buy-tip {
  font-size: 12px;
  color: #909399;
  margin: 12px 0 0 0;
}
.desc-card {
  background-color: #fff;
  border-radius: 8px;
  padding: 25px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-top: 20px;
}
.section-title {
  font-size: 17px;
  color: #303133;
  font-weight: 500;
  padding-left: 10px;
  border-left: 3px solid #409EFF;
  line-height: 20px;
  margin-bottom: 20px;
}
.rich-content {
  font-size: 14px;
  color: #606266;
  line-height: 1.8;
  word-break: break-word;
}
.rich-content >>> img {
  max-width: 100%;
  height: auto;
}
.empty-tip {
  text-align: center;
  color: #909399;
  padding: 60px 0;
  font-size: 14px;
}
.empty-tip.small {
  padding: 20px 0;
}
</style>
